import React, { useRef, useState } from "react";
import {
  Calendar as BigCalendar,
  View,
  momentLocalizer,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useStyles from "./calendar.style";
import "moment/locale/hu";
import {
  MyLeaveCalendarDateHeader,
  MyLeaveCalendarEventWrapper,
  MyLeaveCalendarMonthDateHeader,
  MyLeaveCalendarMonthEventWrapper,
  MyLeaveCalendarTimelineDateHeader,
  MyLeaveCalendarTimelineDayHeader,
  MyLeaveCalendarTimelineView,
  MyLeaveCalendarTimelineWeekWrapper,
  MyLeaveCalendarToolbar,
  MyLeaveCalendarWeeklyTimelineDateHeader,
  MyLeaveCalendarWeeklyTimelineDayHeader,
  MyLeaveCalendarWeeklyTimelineView,
} from "my-leave/components";
import { OcCalendarDayHeader } from "oc/components";
import { useOcContractCalendar, useOcCurrentUserState } from "oc/hooks";

type MyLeaveCalendarProps = {
  height?: number | string;
  currentDate: Date | string;
  onCurrentDateChange: (date: Date) => void;
  events: any[];
  onEventSelected: (event: any) => void;
  defaultView?: View;
  monthFiltering?: boolean;
  setToolbarButton?: (button: string) => void;
  handleButtonChange?: (button: string) => void;
  handleAgendaViewClick?: (event: any) => React.ReactNode;
  loading?: boolean;
};

export const MyLeaveCalendar: React.FC<MyLeaveCalendarProps> = ({
  height,
  currentDate,
  onCurrentDateChange,
  events,
  onEventSelected,
  defaultView,
  monthFiltering,
  setToolbarButton,
  handleButtonChange,
  handleAgendaViewClick,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  let { contractCode } = useOcCurrentUserState();
  const formats = {
    monthHeaderFormat: "YYYY MMMM",
    agendaDateFormat: "MMM.DD. dddd",
    dateFormat: "D",
    weekdayFormat: "dddd",
  };

  const localizer = momentLocalizer(moment);
  (localizer as any).segmentOffset = 0;

  const classes = useStyles();

  const culture = i18n.language === "hu" ? "hu" : "en";

  const eventPropGetter = (event: any) => {
    let actualClass = `rbc-event-${event.state}-${event.leaveType}`;
    return { className: actualClass };
  };

  let [, setContainerHeight] = useState(height || 900);
  let calendarRef = useRef<any>(null);

  let messages = {
    date: t("OC_CALENDAR_DATE"),
    time: t("OC_CALENDAR_TIME"),
    event: t("OC_CALENDAR_EVENT"),
    allDay: t("OC_CALENDAR_ALL_DAY"),
    week: t("OC_CALENDAR_WEEK"),
    work_week: t("OC_CALENDAR_WORK_WEEK"),
    day: t("OC_CALENDAR_DAY"),
    month: t("OC_CALENDAR_MONTH"),
    previous: t("OC_CALENDAR_PREVIOUS"),
    next: t("OC_CALENDAR_NEXT"),
    yesterday: t("OC_CALENDAR_YESTERDAY"),
    tomorrow: t("OC_CALENDAR_TOMORROW"),
    today: t("OC_TODAY"),
    agenda: t("OC_CALENDAR_AGENDA"),
    noEventsInRange: t("OC_CALENDAR_NO_EVENTS_IN_RANGE"),
    showMore: (total: number) => `+${total} ${t("OC_CALENDAR_MORE")}`,
  };

  const date = new Date();
  const firstDate = moment(date).startOf("year").toDate();
  const lastDate = moment(date).endOf("year").toDate();

  const { contractCalendar } = useOcContractCalendar({
    contract: contractCode,
    startDate: firstDate.toISOString(),
    endDate: lastDate.toISOString(),
  });

  const customBackground = (date: Date) => {
    if (date && contractCalendar) {
      const day = moment(date).dayOfYear();
      const type = contractCalendar[day - 1];
      if (type === "P") {
        return "rbc-weekend-day";
      }
      if (type === "F") {
        return "rbc-payed-holiday-day";
      }
    }
    return "";
  };

  return (
    <div style={{ height: "auto" }} className={classes.root}>
      <BigCalendar
        date={new Date(currentDate)}
        onNavigate={(date) => onCurrentDateChange(date)}
        localizer={localizer}
        culture={culture}
        events={events}
        onSelectEvent={onEventSelected}
        tooltipAccessor={null}
        // A custom view kulcsok miatt cast-olunk
        views={
          {
            day: true,
            month: true,
            week: true,
            work_week: true,
            agenda: loading ? false : true,
            timeline: MyLeaveCalendarTimelineView,
            weeklyTimeline: MyLeaveCalendarWeeklyTimelineView,
          } as any
        }
        defaultView={defaultView}
        startAccessor="start"
        endAccessor="end"
        toolbar={true}
        // A components-ben nem szabványos kulcsok is vannak -> cast
        components={
          {
            toolbar: (props: any) => (
              <MyLeaveCalendarToolbar
                {...props}
                currentDate={currentDate}
                onCurrentDateChange={onCurrentDateChange}
                monthFiltering={!!monthFiltering}
                setToolbarButton={setToolbarButton ?? (() => { })}
                handleButtonChange={handleButtonChange as any}
              />
            ),
            eventWrapper: MyLeaveCalendarEventWrapper,
            day: {
              header: OcCalendarDayHeader,
              dateHeader: MyLeaveCalendarDateHeader,
            },
            week: {
              header: OcCalendarDayHeader,
              dateHeader: MyLeaveCalendarDateHeader,
            },
            month: {
              header: OcCalendarDayHeader,
              dateHeader: MyLeaveCalendarMonthDateHeader,
              eventWrapper: MyLeaveCalendarMonthEventWrapper,
            },
            timeline: {
              header: MyLeaveCalendarTimelineDayHeader,
              weekWrapper: MyLeaveCalendarTimelineWeekWrapper,
              dateHeader: MyLeaveCalendarTimelineDateHeader,
            },
            weeklyTimeline: {
              header: MyLeaveCalendarWeeklyTimelineDayHeader,
              dateHeader: MyLeaveCalendarWeeklyTimelineDateHeader,
            },
            agenda: {
              event: handleAgendaViewClick as any,
            },
          } as any
        }
        eventPropGetter={eventPropGetter}
        messages={messages}
        formats={formats}
        onView={() => {
          setContainerHeight("fit-content" as any);
        }}
        ref={calendarRef}
        drilldownView="agenda"
        slotPropGetter={(date) => {
          return { className: customBackground(date) };
        }}
        dayPropGetter={(date) => {
          return { className: customBackground(date) };
        }}
      />
    </div>
  );
};

export default MyLeaveCalendar;
